// LazyPages.jsx - Lazy-loaded pagina's voor betere performance
import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Loading component - snel en lichtgewicht
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      <span className="text-sm text-slate-500">Laden...</span>
    </div>
  </div>
);

// Lazy load FullScreenPages met dynamic import
const FullScreenPagesModule = lazy(() => 
  import('./FullScreenPages').then(module => ({
    default: module
  }))
);

// Wrapper component dat lazy loading handelt
export const LazyPageWrapper = ({ component: ComponentName, ...props }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <LazyPageContent componentName={ComponentName} {...props} />
    </Suspense>
  );
};

// Helper component die de juiste pagina rendert
const LazyPageContent = ({ componentName, ...props }) => {
  // We importeren de specifieke component wanneer nodig
  const Component = lazy(() =>
    import('./FullScreenPages').then(module => ({
      default: module[componentName]
    }))
  );

  return (
    <Suspense fallback={<PageLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

// Export individuele lazy components voor direct gebruik
export const LazyPBGesprekkenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.PBGesprekkenPage }))
);

export const LazyWeekmenuPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.WeekmenuPage }))
);

export const LazyBoodschappenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.BoodschappenPage }))
);

export const LazyCorveePage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.CorveePage }))
);

export const LazyHuisafsprakenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.HuisafsprakenPage }))
);

export const LazyTeamuitjesPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.TeamuitjesPage }))
);

export const LazyVergaderingenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.VergaderingenPage }))
);

export const LazyFichesPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.FichesPage }))
);

export const LazyTakenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.TakenPage }))
);

export const LazyClientenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.ClientenPage }))
);

export const LazyTeamoverlegPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.TeamoverlegPage }))
);

export const LazyMDOPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.MDOPage }))
);

export const LazyMedicatiePage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.MedicatiePage }))
);

export const LazyChecklistPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.ChecklistPage }))
);

export const LazyOverdrachtPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.OverdrachtPage }))
);

export const LazyWifiPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.WifiPage }))
);

export const LazyAfvalkalenderPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.AfvalkalenderPage }))
);

export const LazyZorgplanEvaluatiesPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.ZorgplanEvaluatiesPage }))
);

export const LazyRIEEvaluatiesPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RIEEvaluatiesPage }))
);

export const LazyRevalidatiePage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RevalidatiePage }))
);

export const LazyRevalidatieClientPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RevalidatieClientPage }))
);

export const LazyEvaluatieOverzichtPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.EvaluatieOverzichtPage }))
);

export const LazyRoosterPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RoosterPage }))
);

export const LazyAfsprakenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.AfsprakenPage }))
);

export const LazyKlusjesPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.KlusjesPage }))
);

export const LazyDagbestedingPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.DagbestedingPage }))
);

export const LazyEetmomentenPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.EetmomentenPage }))
);

export const LazyVoiceRapportagePage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.VoiceRapportagePage }))
);

export const LazyRapporteerHelperPage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RapporteerHelperPage }))
);

export const LazyRapportageControlePage = lazy(() =>
  import('./FullScreenPages').then(m => ({ default: m.RapportageControlePage }))
);

export const LazyInwerkbotPage = lazy(() =>
  import('../../pages/dashboard/InwerkbotPage').then(m => ({ default: m.default }))
);

// Suspense wrapper voor alle lazy components
export const withSuspense = (Component) => {
  return function SuspenseWrapper(props) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Component {...props} />
      </Suspense>
    );
  };
};

export { PageLoader };
